import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { STATUS_COLOURS } from '../lib/theme';

/**
 * Connection indicator.
 *
 * Tracks whether the API is actually reachable, not just `navigator.onLine` —
 * which only reports that a network interface exists, and stays true on a
 * captive-portal wifi or when the Worker is down. Field crews on patchy mobile
 * coverage need to know their last action reached the server.
 *
 * `inline` is a glowing dot plus coloured label, for the header strip. `chip`
 * adds a tinted pill background, for when it stands alone.
 */
export function StatusPill({ variant = 'inline' }: { variant?: 'inline' | 'chip' }): React.JSX.Element {
    const [online, setOnline] = useState(() => navigator.onLine);
    const [reachable, setReachable] = useState(true);

    useEffect(() => {
        const update = (): void => {
            setOnline(navigator.onLine);
        };
        window.addEventListener('online', update);
        window.addEventListener('offline', update);
        return () => {
            window.removeEventListener('online', update);
            window.removeEventListener('offline', update);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const check = async (): Promise<void> => {
            try {
                const res = await fetch('/api/health', { cache: 'no-store' });
                if (!cancelled) {
                    setReachable(res.ok);
                }
            } catch {
                if (!cancelled) {
                    setReachable(false);
                }
            }
        };
        void check();
        const t = setInterval(() => {
            void check();
        }, 60_000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, []);

    const connected = online && reachable;
    const colour = connected ? STATUS_COLOURS.ok : STATUS_COLOURS.error;
    const label = connected ? 'Internet Connected' : 'Internet Disconnected';

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'nowrap',
                ...(variant === 'chip'
                    ? {
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha(colour, 0.35),
                          bgcolor: alpha(colour, 0.12),
                      }
                    : {}),
            }}
        >
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: colour,
                    boxShadow: `0 0 6px ${colour}`,
                    flexShrink: 0,
                }}
            />
            <Typography sx={{ color: colour, fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                {label}
            </Typography>
        </Box>
    );
}
